import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isInApp } from '../utils/isInApp';
import './AppPaymentBlocker.css';

/**
 * 모바일 앱(TWA / Capacitor) 환경에서 결제 페이지를 차단하고
 * 단순 안내 텍스트만 보여주는 가드 컴포넌트.
 *
 * # 정책 컴플라이언스 메모
 * - Apple App Store Guideline 3.1.1: "Apps may not direct customers to external
 *   purchasing mechanisms." → 앱 안에서 외부 결제 사이트로 즉시 이동시키는
 *   버튼/링크는 거절 사유가 될 수 있음.
 * - 따라서 본 컴포넌트는 결제 사이트 도메인을 텍스트로만 안내하고,
 *   클릭 가능한 외부 결제 링크/버튼은 노출하지 않음.
 * - 한국 시장 한정으로 StoreKit External Purchase Entitlement(별도 신청)를
 *   받으면 외부 결제 링크 노출이 가능하지만 본 단계에서는 미적용.
 *
 * # 매출 영향
 * - 자사 분석상 결제의 절대 다수가 PC에서 이뤄지므로 영향은 제한적.
 * - 추후 entitlement 획득 시 외부 링크 버튼을 추가할 수 있도록 구조 유지.
 *
 * 사용 예시:
 *   <AppPaymentBlocker pageName="PRO 구독">
 *     <ExistingPaymentUI />
 *   </AppPaymentBlocker>
 */
const AppPaymentBlocker = ({
  children,
  pageName = '구독',
  fallbackPath = '/',
}) => {
  const navigate = useNavigate();
  const blocked = isInApp();

  if (!blocked) {
    return <>{children}</>;
  }

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  return (
    <div className="app-payment-blocker">
      <div className="app-payment-blocker__card">
        <div className="app-payment-blocker__icon" aria-hidden="true">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="6" width="18" height="13" rx="2" stroke="#3182F6" strokeWidth="1.8" />
            <path d="M3 10H21" stroke="#3182F6" strokeWidth="1.8" />
            <path d="M7 15H10" stroke="#3182F6" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>

        <h2 className="app-payment-blocker__title">앱에서는 {pageName} 결제를 이용할 수 없어요</h2>

        <p className="app-payment-blocker__description">
          PC 또는 모바일 웹브라우저에서
          <br />
          <strong>whatquiz.co.kr</strong> 에 접속해
          <br />
          같은 계정으로 로그인 후 가입할 수 있습니다.
        </p>

        <div className="app-payment-blocker__steps">
          <div className="app-payment-blocker__step">
            <span className="app-payment-blocker__step-num">1</span>
            <span>웹브라우저에서 whatquiz.co.kr 접속</span>
          </div>
          <div className="app-payment-blocker__step">
            <span className="app-payment-blocker__step-num">2</span>
            <span>같은 계정으로 로그인 후 구독 가입</span>
          </div>
          <div className="app-payment-blocker__step">
            <span className="app-payment-blocker__step-num">3</span>
            <span>앱으로 돌아오면 자동으로 기능이 활성화됩니다</span>
          </div>
        </div>

        <button type="button" className="app-payment-blocker__secondary" onClick={handleGoBack}>
          확인
        </button>

        <p className="app-payment-blocker__notice">
          결제 관련 문의: <a href="tel:02-949-9158">02-949-9158</a>
          <br />
          평일 10:00 ~ 18:00 (점심 12:00 ~ 13:00 제외)
        </p>
      </div>
    </div>
  );
};

export default AppPaymentBlocker;
